import { DatePicker, Input, MultiSelect, Select, Text, type TypeSelectOption } from '@thefarmersfront/kitchen';

import clsx from 'clsx';
import dayjs from 'dayjs';

import { SearchFilter } from '~/components/common';

import { getDateByOffset } from '~/utils';

import { DELIVERY_AGENCY_SHIPPING_LABEL_STATUS, DELIVERY_AGENCY_TRACKING_PRINT_STATUS, OPTION_ALL } from '~/constants';

import { DeliveryAgencyEstimatedDeliveryDateLabel } from '../../DeliveryAgencyEstimatedDeliveryDateLabel';

import type {
  TypeDeliveryAgencyTrackingFilterOptions,
  TypeDeliveryAgencyTrackingFilterValues,
  TypeDeliveryAgencyTrackingFilterValuesStatus,
} from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyTrackingFilter.module.scss';

const DeliveryAgencyTrackingFilter = ({
  options,
  values,
  valuesStatus,
  onChange,
  onChangeStatus,
  onSearch,
  onReset,
}: {
  options: TypeDeliveryAgencyTrackingFilterOptions;
  values: TypeDeliveryAgencyTrackingFilterValues;
  valuesStatus: TypeDeliveryAgencyTrackingFilterValuesStatus;
  onChange: (values: Partial<TypeDeliveryAgencyTrackingFilterValues>) => void;
  onChangeStatus: (values: Partial<TypeDeliveryAgencyTrackingFilterValuesStatus>) => void;
  onSearch: () => void;
  onReset: () => void;
}) => {
  const hasDeliveryFailedStatus = values.trackingStatuses.some(
    (status) =>
      status.value === OPTION_ALL.value || status.value === DELIVERY_AGENCY_SHIPPING_LABEL_STATUS.DELIVERY_FAILED
  );

  return (
    <SearchFilter formId="delivery-agency-tracking-search-form" onSubmit={onSearch} onReset={onReset}>
      <div className={clsx(styles['filter-container'], styles['filter-keyword'])}>
        <Text size="small" weight="semi-bold" element="p">
          키워드
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options.searchKeywordTypes}
            value={values.keywordType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.keywordType?.value) return;
              onChange({ keywordType: value, keyword: '' });
              onChangeStatus({ keyword: 'default' });
            }}
          />
          <Input
            type="text"
            size="small"
            placeholder="입력"
            maxLength={50}
            value={values.keyword}
            status={valuesStatus.keyword}
            onChange={(e) => onChange({ keyword: e.target.value })}
            onBlur={(e) => onChange({ keyword: e.target.value.trim() })}
            onFocus={() => onChangeStatus({ keyword: 'default' })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-date'])}>
        <DeliveryAgencyEstimatedDeliveryDateLabel />
        <div className={styles['input-container']}>
          <DatePicker
            size="small"
            placeholder="날짜 선택"
            minDate={dayjs().subtract(3, 'month').startOf('month').toDate()}
            maxDate={getDateByOffset(2, 'day')}
            value={values.deliveryDate}
            status={valuesStatus.deliveryDate}
            onChange={(value) => onChange({ deliveryDate: value })}
            onFocus={() => onChangeStatus({ deliveryDate: 'default' })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-tracking-status'])}>
        <Text size="small" weight="semi-bold" element="p">
          운송장 상태
        </Text>
        <div className={styles['input-container']}>
          <MultiSelect
            size="small"
            placeholder="선택 (복수 선택 가능)"
            options={[OPTION_ALL, ...options.trackingStatuses]}
            value={values.trackingStatuses}
            allOption={OPTION_ALL}
            onChange={(_, value) =>
              onChange({ trackingStatuses: value ?? [], deliveryFailureReasonCategories: [OPTION_ALL] })
            }
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-delivery-failure-reason-categories'])}>
        <Text size="small" weight="semi-bold" element="p">
          배송 실패 사유
        </Text>
        <div className={styles['input-container']}>
          <MultiSelect
            size="small"
            placeholder="선택 (복수 선택 가능)"
            disabled={!hasDeliveryFailedStatus}
            options={[OPTION_ALL, ...options.deliveryFailureReasonCategories]}
            value={values.deliveryFailureReasonCategories}
            onChange={(_, value) => onChange({ deliveryFailureReasonCategories: value ?? [] })}
            allOption={OPTION_ALL}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-delivery-type'])}>
        <Text size="small" weight="semi-bold" element="p">
          배송 유형
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={[OPTION_ALL, ...options.deliveryTypes]}
            value={values.deliveryType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => onChange({ deliveryType: value })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-tracking-print-status'])}>
        <Text size="small" weight="semi-bold" element="p">
          운송장 출력
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={[OPTION_ALL, ...Object.values(DELIVERY_AGENCY_TRACKING_PRINT_STATUS)]}
            value={values.trackingPrintStatus}
            onChange={(_: unknown, value: TypeSelectOption<string>) => onChange({ trackingPrintStatus: value })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-courier'])}>
        <Text size="small" weight="semi-bold" element="p">
          배송사
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={[OPTION_ALL, ...options.courierCodes]}
            value={values.courier}
            onChange={(_: unknown, value: TypeSelectOption<string>) => onChange({ courier: value })}
          />
        </div>
      </div>
    </SearchFilter>
  );
};

export default DeliveryAgencyTrackingFilter;
